import { Props as DatePickerPopoverProps } from './DatePickerPopover';
import { DatePickerPopover, TimeRangePicker } from '#components';
import { formatToTimeZone, IsoTimestamp, addMilliseconds } from '#datetime';
import { DateIcon } from '#icons';
import { Button, Stack } from '@mui/material';
import { differenceInMilliseconds } from 'date-fns';
import { useCallback, useRef, useState } from 'react';

type Props = {
  /** Whether or not this is being rendered on mobile sizes */
  isMobile?: boolean;
  value: { start: IsoTimestamp; end: IsoTimestamp };
  timezone: string;
  coalesceTimeUpdates?: boolean;
  hideClockIcons?: boolean;
  disableFuture?: DatePickerPopoverProps['disableFuture'];
  disablePast?: DatePickerPopoverProps['disablePast'];
  onChange: (startTimestamp: IsoTimestamp, endTimestamp: IsoTimestamp) => void;
};

const DateTimeRangePicker = ({
  isMobile,
  value: { start, end },
  timezone,
  coalesceTimeUpdates,
  hideClockIcons,
  disableFuture,
  disablePast,
  onChange,
}: Props) => {
  const datePickerRef = useRef<HTMLButtonElement>(null);
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  const handleDateChange = useCallback(
    (date: IsoTimestamp) => {
      const diffInMillis = differenceInMilliseconds(end, start);
      const newEnd = addMilliseconds(date, timezone, diffInMillis);

      onChange(date, newEnd);
      setDatePickerOpen(false);
    },
    [end, onChange, start, timezone]
  );

  const handleTimeChange = useCallback(
    (sHour: IsoTimestamp, eHour: IsoTimestamp) => {
      onChange(sHour, eHour);
    },
    [onChange]
  );

  return (
    <Stack direction="row" spacing={1} flexGrow={1} flexWrap={isMobile ? 'wrap' : 'nowrap'}>
      <Button
        ref={datePickerRef}
        fullWidth={isMobile}
        startIcon={<DateIcon />}
        onClick={() => setDatePickerOpen(true)}
        // eslint-disable-next-line no-restricted-syntax
        sx={{ borderRadius: '6px', width: '140px', justifyContent: 'flex-start' }}
      >
        {formatToTimeZone(start, 'MMM d yyyy', timezone)}
      </Button>
      {datePickerOpen && (
        <DatePickerPopover
          open
          date={start}
          anchorEl={datePickerRef.current}
          onClose={() => setDatePickerOpen(false)}
          timezone={timezone}
          onChange={handleDateChange}
          disableFuture={disableFuture}
          disablePast={disablePast}
          shouldPreserveTime
        />
      )}

      <TimeRangePicker
        value={{ start, end }}
        timezone={timezone}
        coalesceTimeUpdates={coalesceTimeUpdates}
        hideClockIcons={hideClockIcons}
        onChange={handleTimeChange}
      />
    </Stack>
  );
};

export default DateTimeRangePicker;
